/**
 * Frontend Input Validation — Defense-in-Depth
 *
 * These validators mirror the server-side validation in functions/src/middleware/validation.ts.
 * They provide instant feedback to users and prevent unnecessary round-trips.
 *
 * NOTE: Server-side validation is the security boundary. These are UX improvements.
 */

// ─── Regex Patterns ───
// Matches server-side phone validation in functions/src/middleware/validation.ts
export const PHONE_REGEX = /^(\+?880)?01[3-9]\d{8}$/;
export const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;
export const IGN_REGEX = /^[a-zA-Z0-9_\-.\s]{2,30}$/;
export const TRANSACTION_ID_REGEX = /^[a-zA-Z0-9]{4,30}$/;

// ─── Constants (mirrored from server) ───
export const MIN_DEPOSIT = 100;
export const MAX_DEPOSIT = 50_000;
export const MIN_WITHDRAWAL = 200;
export const MAX_WITHDRAWAL = 100_000;
export const VALID_PAYMENT_METHODS = ["bKash", "Nagad", "Rocket"] as const;

// ─── Validators ───
// Each returns { valid: boolean, message?: string }

export interface ValidationResult {
    valid: boolean;
    message?: string;
}

export function validateUsername(value: string): ValidationResult {
    const trimmed = value.trim();
    if (trimmed.length === 0) return { valid: false, message: "Username is required." };
    if (trimmed.length < 3) return { valid: false, message: "Username must be at least 3 characters." };
    if (trimmed.length > 20) return { valid: false, message: "Username must be 20 characters or less." };
    if (!USERNAME_REGEX.test(trimmed)) {
        return { valid: false, message: "Username can only contain letters, numbers, and underscores." };
    }
    return { valid: true };
}

export function validatePhone(value: string): ValidationResult {
    if (!value.trim()) return { valid: true }; // Phone is optional on registration
    const cleaned = value.replace(/[\s-]/g, "");
    if (!PHONE_REGEX.test(cleaned)) {
        return { valid: false, message: "Invalid phone format. Use 01XXXXXXXXX or +88001XXXXXXXXX." };
    }
    return { valid: true };
}

export function validatePhoneRequired(value: string): ValidationResult {
    const cleaned = value.replace(/[\s-]/g, "");
    if (!cleaned) return { valid: false, message: "Phone number is required." };
    if (!PHONE_REGEX.test(cleaned)) {
        return { valid: false, message: "Invalid phone format. Use 01XXXXXXXXX or +88001XXXXXXXXX." };
    }
    return { valid: true };
}

export function validateIgn(value: string): ValidationResult {
    const trimmed = value.trim();
    if (!trimmed) return { valid: true }; // IGN is optional
    if (trimmed.length < 2) return { valid: false, message: "IGN must be at least 2 characters." };
    if (trimmed.length > 30) return { valid: false, message: "IGN must be 30 characters or less." };
    if (!IGN_REGEX.test(trimmed)) {
        return { valid: false, message: "IGN can only contain letters, numbers, underscores, hyphens, dots, and spaces." };
    }
    return { valid: true };
}

export function validateDepositAmount(value: number): ValidationResult {
    if (!Number.isInteger(value) || value <= 0) {
        return { valid: false, message: "Amount must be a positive whole number." };
    }
    if (value < MIN_DEPOSIT) return { valid: false, message: `Minimum deposit is T ${MIN_DEPOSIT}.` };
    if (value > MAX_DEPOSIT) return { valid: false, message: `Maximum deposit is T ${MAX_DEPOSIT.toLocaleString()}.` };
    return { valid: true };
}

export function validateWithdrawalAmount(value: number, availableBalance: number): ValidationResult {
    if (!Number.isInteger(value) || value <= 0) {
        return { valid: false, message: "Amount must be a positive whole number." };
    }
    if (value < MIN_WITHDRAWAL) return { valid: false, message: `Minimum withdrawal is T ${MIN_WITHDRAWAL}.` };
    if (value > MAX_WITHDRAWAL) return { valid: false, message: `Maximum withdrawal is T ${MAX_WITHDRAWAL.toLocaleString()}.` };
    if (value > availableBalance) return { valid: false, message: "Insufficient balance." };
    return { valid: true };
}

export function validateTransactionId(value: string): ValidationResult {
    const trimmed = value.trim();
    if (!trimmed) return { valid: false, message: "Transaction ID is required." };
    if (!TRANSACTION_ID_REGEX.test(trimmed)) {
        return { valid: false, message: "Transaction ID must be 4-30 alphanumeric characters." };
    }
    return { valid: true };
}



/**
 * Sanitize a string by trimming whitespace and removing control characters.
 * Does NOT strip HTML tags — React's JSX auto-escaping handles that.
 */
export function sanitize(value: string): string {
    // eslint-disable-next-line no-control-regex
    return value.trim().replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
}
